/**
 * The session shape stored under `skileo.session` (wp/shell §1.4, §2, ARCHITECTURE.md §8) and the mapping from the
 * `POST users/publicLogin` answer. Pure TypeScript (no Vue), so the Playwright fixtures can seed the same shape.
 */

/**
 * The employer codes. `on` and `en` are no softwares of their own since 2026-11-03 (owner's call, BR-097: Onboarding
 * and Engagement are part of Human resources, the API lists three apps) — they stay here as codes the data and old
 * addresses still carry: `SOFTWARE_IDS` (task and document flags, mailboxes), the `/on/…` and `/en/…` redirects.
 */
export const EMPLOYER_SOFTWARES = ['re', 'on', 'hr', 'en', 'ac'] as const
export const MERGED_INTO_HR = ['on', 'en'] as const
export const FREELANCER_SOFTWARES = ['ca', 'em'] as const
export const SOFTWARES = [...EMPLOYER_SOFTWARES, ...FREELANCER_SOFTWARES] as const

export type EmployerSoftware = (typeof EMPLOYER_SOFTWARES)[number]
export type FreelancerSoftware = (typeof FREELANCER_SOFTWARES)[number]
export type SoftwareCode = (typeof SOFTWARES)[number]

/** `software_id` per short code (legacy `authService.js:23-32`; `ca`/`em` reuse 1/2 for a Freelancer). */
export const SOFTWARE_IDS: Readonly<Record<SoftwareCode, number>> = { re: 1, on: 2, hr: 3, en: 4, ac: 5, ca: 1, em: 2 }

export type UserType = 'Client' | 'Staff' | 'Freelancer' | 'User'

export function isSoftwareCode(value: unknown): value is SoftwareCode {
  return typeof value === 'string' && (SOFTWARES as readonly string[]).includes(value)
}

export interface Workspace {
  Id: number
  Label: string
  LogoUrl?: string | null
  /** (sic) 1 when the company-profile wizard is complete. */
  WizzardCompleted?: number | null
  [field: string]: unknown
}

/**
 * A workspace is set up once the API says its profile is complete (`IsCompleted === 1`) and its setup wizard was
 * finished (`WizzardCompleted`) — the company profile shows its tabs, and the app its vertical layout; otherwise the
 * profile is the wizard, framed by the onboarding layout (legacy `companyIsSetup`, wp/company §1.2).
 */
export function isWorkspaceSetUp(workspace: { IsCompleted?: unknown; WizzardCompleted?: unknown } | null): boolean {
  return Boolean(workspace) && workspace?.IsCompleted === 1 && Boolean(workspace?.WizzardCompleted)
}

/** One entry of `menu.apps`. */
export interface Software {
  id: number
  title: string
  short: SoftwareCode
  color: string
}

/** Staff permission matrix (`menu.user.Permissions`): software → modules → `acl.read` / `acl.write`. */
export type PermissionMatrix = Record<
  string,
  {
    short?: string
    label?: string
    modules?: Record<string, { label?: string; acl?: { read?: boolean | number; write?: boolean | number } }>
  }
>

/** The account (`menu.user`, `GET users/profile`). Only the fields the shell reads are typed. */
export interface Profile {
  Id: number
  FirstName?: string | null
  LastName?: string | null
  Email?: string | null
  ImageUrl?: string | null
  Language?: string | null
  /** PHP date-format key (`m/d/Y`, `d/m/Y`, `Y/m/d`). */
  Timeformat?: string | null
  Permissions?: PermissionMatrix | null
  [field: string]: unknown
}

export interface Session {
  /** 64-hex token sent as `Auth-Key`; 24 h from minting, no refresh exists. */
  token: string
  userType: UserType
  currentSoftware: SoftwareCode | null
  /** `null` when the account has none (the API answers a Workspace with `Id` 0). */
  currentWorkspace: Workspace | null
  workspaces: Workspace[]
  softwares: Software[]
  profile: Profile
  /** Staff only. */
  permissions: PermissionMatrix | null
  /** Moment-style display pattern from `settings/timeformats` (e.g. `DD/MM/YYYY`); `null` until loaded. */
  dateFormat: string | null
  /** PHP key of the same format (legacy `dateFormatFlat`). */
  dateFormatKey: string | null
}

/** The `data` of a `users/publicLogin` session answer (the second-factor step has `token === 'pin2fa'`). */
export interface LoginSessionData {
  token: string
  user_type: 'Client' | 'Staff' | 'Freelancer'
  /** (sic) */
  current_sotftware: SoftwareCode
  current_workspace: Workspace
  redirect_url?: string
  menu: {
    user: Profile
    apps: Record<string, Software>
    workspaces: Workspace[]
    current_workspace?: number
  }
}

/** The `data` of a `users/publicLogin` answer at the second-factor step (no session yet). */
export interface SecondFactorData {
  token: 'pin2fa'
  /** `sms` / `whatsapp`, or `Email` when the code was e-mailed (asked for, or the SMS failed). */
  Channel2fa: string | null
  /** 1 when the account may ask for the code by e-mail. */
  EmailEnabled2fa: number | null
}

/** Everything `POST users/publicLogin` answers with on HTTP 200. */
export type LoginAnswer = LoginSessionData | SecondFactorData

export function isSecondFactorStep(data: { token?: unknown }): data is SecondFactorData {
  return data.token === 'pin2fa'
}

export function workspaceOrNull(workspace: Workspace | null | undefined): Workspace | null {
  return workspace && Number(workspace.Id) > 0 ? workspace : null
}

export function sessionFromLoginResponse(data: LoginSessionData): Session {
  if (isSecondFactorStep(data)) throw new Error('The login answer is a second-factor step, not a session')
  return {
    token: data.token,
    userType: data.user_type,
    currentSoftware: isSoftwareCode(data.current_sotftware) ? data.current_sotftware : null,
    currentWorkspace: workspaceOrNull(data.current_workspace),
    workspaces: data.menu.workspaces ?? [],
    softwares: Object.values(data.menu.apps ?? {}),
    profile: data.menu.user,
    permissions: data.menu.user.Permissions ?? null,
    dateFormat: null,
    dateFormatKey: data.menu.user.Timeformat ?? null,
  }
}

/** Per-viewer preferences that survive logout (legacy kept `locale`, `list-<name>`, `dontRemind<name>`). */
export interface Preferences {
  locale: string | null
  /** Column preferences per list (legacy `list-<name>`). */
  columns: Record<string, unknown>
  /** "Don't remind me" flags (legacy `dontRemind<name>`). */
  dontRemind: Record<string, boolean>
}

/** The JSON stored under `localStorage['skileo.session']`. */
export interface StoredState {
  version: 1
  session: Session | null
  prefs: Preferences
}

export const STORAGE_KEY = 'skileo.session'

export function emptyPreferences(): Preferences {
  return { locale: null, columns: {}, dontRemind: {} }
}
